import { toast } from 'react-hot-toast';
import { CalendarClock } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUser } from '@/shared/hooks/useAuth';
import {
  useTodayLog,
  useMyLogs,
  useTeamAttendance,
  useAttendanceMutations,
} from '../hooks/useAttendance';
import { MyAttendanceCard } from '../components/MyAttendanceCard';
import { TeamAttendance } from '../components/TeamAttendance';
import type { BreakType, TimeLog } from '../types';

function fmtLogDate(d?: string) {
  if (!d) return '—';
  const date = new Date(`${d}T00:00:00`);
  return Number.isNaN(date.getTime()) ? d : date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function AttendancePage() {
  const user = useUser();
  const isPrivileged = ['admin', 'superadmin'].includes(user?.role ?? '');

  const { data: today, isLoading: todayLoading } = useTodayLog();
  const { data: logs = [], isLoading: logsLoading } = useMyLogs(20);
  const { data: team = [], isLoading: teamLoading, isError: teamError } = useTeamAttendance(isPrivileged);
  const { checkIn, checkOut, startBreak, endBreak } = useAttendanceMutations();

  const busy = checkIn.isPending || checkOut.isPending || startBreak.isPending || endBreak.isPending;

  const handleCheckIn = () =>
    checkIn.mutate(undefined, {
      onSuccess: (d) => toast.success(`Checked in at ${d.time}`),
      onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to check in.'),
    });

  const handleCheckOut = () =>
    checkOut.mutate(undefined, {
      onSuccess: (d) => toast.success(`Checked out at ${d.time} · ${d.hours}h`),
      onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to check out.'),
    });

  const handleStartBreak = (type: BreakType) =>
    startBreak.mutate(type, {
      onSuccess: () => toast.success(`Started ${type} break`),
      onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to start break.'),
    });

  const handleEndBreak = () =>
    endBreak.mutate(undefined, {
      onSuccess: (d) => toast.success(`Break ended (${Math.round((d.duration || 0) / 60)} min)`),
      onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to end break.'),
    });

  const logColumns: Column<TimeLog>[] = [
    { key: 'log_date', header: 'Date', render: (l) => fmtLogDate(l.log_date) },
    { key: 'check_in', header: 'Check In', render: (l) => l.check_in || '—' },
    { key: 'check_out', header: 'Check Out', render: (l) => l.check_out || '—' },
    { key: 'hours', header: 'Hours', align: 'right', render: (l) => (l.hours != null && l.hours > 0 ? `${l.hours}h` : '—') },
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex items-center gap-3">
        <CalendarClock className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-display-sm font-semibold text-on-surface">Attendance</h1>
          <p className="text-body-sm text-on-surface-variant">Check in, take breaks, and track your hours.</p>
        </div>
      </div>

      {/* Section 1 — My Attendance (always visible) */}
      <MyAttendanceCard
        today={today}
        loading={todayLoading}
        busy={busy}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
        onStartBreak={handleStartBreak}
        onEndBreak={handleEndBreak}
      />

      {/* Recent logs */}
      <Card>
        <div className="border-b border-outline px-5 py-4">
          <h2 className="text-data-lg font-semibold text-on-surface">My Recent Attendance</h2>
        </div>
        <div className="p-5">
          {logsLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <Table columns={logColumns} data={logs} rowKey={(l) => l.id ?? l.log_date} empty="No attendance records yet." />
          )}
        </div>
      </Card>

      {/* Section 2 — Team Attendance (admin / superadmin only) */}
      {isPrivileged && <TeamAttendance team={team} loading={teamLoading} isError={teamError} />}
    </div>
  );
}
